import { useState, useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '@/context/AuthContext'
import * as api from '@/lib/api'
import type { AdminNewsArticle, AdminGroup } from '@/lib/api'
import RowActions from '@/components/RowActions'
import { useConfirm } from '@/components/ConfirmDialog'

export default function NewsPage() {
  const { token, role } = useAuth()
  const navigate = useNavigate()

  const [items, setItems] = useState<AdminNewsArticle[]>([])
  const [groups, setGroups] = useState<AdminGroup[]>([])
  const [filterGroupSlug, setFilterGroupSlug] = useState('')
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const { confirm, dialog } = useConfirm()

  const isAdminRole = role === 'admin' || role === 'superadmin'

  const load = useCallback(async () => {
    if (!token) return
    setLoading(true)
    try {
      setItems(await api.getAdminNews(token))
      setError(null)
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : String(e))
    } finally {
      setLoading(false)
    }
  }, [token])

  useEffect(() => { load() }, [load])

  useEffect(() => {
    if (!token || !isAdminRole) return
    api.getAdminGroups(token).then(setGroups).catch(() => {})
  }, [token, isAdminRole])

  const handleDelete = async (id: string, title: string) => {
    if (!await confirm(`"${title}" akan dihapus permanen dan tidak bisa dipulihkan.`, { title: `Hapus "${title}"` })) return
    if (!token) return
    try {
      await api.deleteNews(token, id)
      load()
    } catch (e: unknown) {
      alert(e instanceof Error ? e.message : String(e))
    }
  }

  const sorted = [...items].sort((a, b) => {
    if (a.is_featured !== b.is_featured) return a.is_featured ? -1 : 1
    return new Date(b.date).getTime() - new Date(a.date).getTime()
  })

  const filtered = filterGroupSlug
    ? sorted.filter((item) => item.group_slug === filterGroupSlug)
    : sorted

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-lg font-semibold text-white">News</h1>
          <p className="text-zinc-500 text-xs mt-0.5">{filtered.length} articles</p>
        </div>
        <div className="flex items-center gap-3">
          {isAdminRole && groups.length > 0 && (
            <select
              value={filterGroupSlug}
              onChange={(e) => setFilterGroupSlug(e.target.value)}
              className="bg-zinc-800 text-white text-xs rounded-lg px-3 py-2 border border-zinc-700 focus:outline-none focus:border-zinc-500"
            >
              <option value="">Semua Group</option>
              {groups.map((g) => (
                <option key={g.id} value={g.slug}>{g.name}</option>
              ))}
            </select>
          )}
          <button
            onClick={() => navigate('/news/create')}
            className="bg-yellow-500 hover:bg-yellow-400 text-black text-xs font-semibold px-4 py-2 rounded transition-colors"
          >
            + Add Article
          </button>
        </div>
      </div>

      {loading && <p className="text-zinc-500 text-sm">Loading…</p>}
      {error && <p className="text-red-400 text-sm">{error}</p>}

      {!loading && !error && filtered.length === 0 && items.length === 0 && (
        <div className="py-16 text-center">
          <p className="text-zinc-500 text-sm">No articles yet.</p>
          <button
            onClick={() => navigate('/news/create')}
            className="mt-3 text-xs text-yellow-400 hover:text-yellow-300 transition-colors"
          >
            + Add the first one
          </button>
        </div>
      )}

      {!loading && !error && items.length > 0 && (
        <div className="bg-zinc-900 rounded-lg border border-zinc-800 overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left border-b border-zinc-800">
                <th className="px-4 py-3 text-xs text-zinc-500 font-medium">Date</th>
                <th className="px-4 py-3 text-xs text-zinc-500 font-medium">Category</th>
                {isAdminRole && <th className="px-4 py-3 text-xs text-zinc-500 font-medium">Group</th>}
                <th className="px-4 py-3 text-xs text-zinc-500 font-medium">Title</th>
                <th className="px-4 py-3 text-xs text-zinc-500 font-medium">Status</th>
                <th className="px-4 py-3 text-xs text-zinc-500 font-medium">Featured</th>
                <th className="px-4 py-3 text-xs text-zinc-500 font-medium w-24">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-800">
              {filtered.map((item) => (
                <tr key={item.id} className="hover:bg-zinc-800/30 transition-colors">
                  <td className="px-4 py-3 text-zinc-400 text-xs whitespace-nowrap">{item.date}</td>
                  <td className="px-4 py-3">
                    <span className="text-[10px] tracking-wide px-2 py-0.5 rounded bg-zinc-800 text-zinc-300">
                      {item.category}
                    </span>
                  </td>
                  {isAdminRole && (
                    <td className="px-4 py-3">
                      {item.group_slug ? (
                        <span className="text-[10px] tracking-wide px-2 py-0.5 rounded bg-zinc-700 text-zinc-300">
                          {item.group_slug}
                        </span>
                      ) : (
                        <span className="text-zinc-600 text-[10px]">—</span>
                      )}
                    </td>
                  )}
                  <td className="px-4 py-3 max-w-xs">
                    <button
                      onClick={() => navigate(`/news/${item.id}`, { state: { article: item } })}
                      className="text-white hover:text-yellow-400 transition-colors text-left truncate block w-full"
                    >
                      {item.title}
                    </button>
                  </td>
                  <td className="px-4 py-3">
                    {item.is_draft ? (
                      <span className="text-[10px] font-medium px-2 py-0.5 rounded-full bg-amber-400/10 text-amber-400 border border-amber-400/20">
                        Draft
                      </span>
                    ) : (
                      <span className="text-[10px] font-medium px-2 py-0.5 rounded-full bg-green-500/10 text-green-400 border border-green-500/20">
                        Published
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-center">
                    {item.is_featured && <span className="text-yellow-400 text-xs">★</span>}
                  </td>
                  <td className="px-4 py-3">
                    <RowActions actions={[
                      { label: 'Edit', onClick: () => navigate(`/news/${item.id}/edit`, { state: { article: item } }) },
                      { label: 'Hapus', onClick: () => handleDelete(item.id, item.title), variant: 'danger' },
                    ]} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {dialog}
    </div>
  )
}
